/**
 * Quality Management — shared shapes + scoring.
 *
 * One scoring function for the AI QM auditor (worker), the supervisor review
 * endpoint (api) and the live preview in the evaluation editor (web), so a
 * score never depends on which of them computed it.
 */
/**
 * Score answers against a snapshot.
 *
 * - Scores are clamped to [0, weight] and rounded to integers — a model that
 *   returns 7 for a weight-5 item, or 3.6, cannot inflate the total.
 * - Not-applicable items are excluded from BOTH total and max, so a call with
 *   no hold does not lose the "hold procedure" points.
 * - A missing answer counts as 0 (not N/A): silence is not a pass.
 * - A critical item scored 0 fails the evaluation regardless of percentage.
 */
export function scoreQmAnswers(items, answers, passingScore) {
    const byId = new Map(answers.map((a) => [a.itemId, a]));
    let totalScore = 0;
    let maxScore = 0;
    const criticalFailures = [];
    const aligned = [];
    for (const item of items) {
        const a = byId.get(item.id);
        const na = a?.notApplicable === true;
        const raw = typeof a?.score === 'number' && Number.isFinite(a.score) ? a.score : 0;
        const score = na ? null : Math.max(0, Math.min(item.weight, Math.round(raw)));
        aligned.push({
            itemId: item.id,
            score,
            ...(na ? { notApplicable: true } : {}),
            ...(a?.comment ? { comment: a.comment } : {}),
            ...(a?.evidence ? { evidence: a.evidence } : {}),
        });
        if (score === null)
            continue;
        totalScore += score;
        maxScore += item.weight;
        if (item.isCritical && score === 0)
            criticalFailures.push(item.id);
    }
    const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 10000) / 100 : 0;
    return {
        totalScore,
        maxScore,
        percentage,
        passed: maxScore > 0 && percentage >= passingScore && criticalFailures.length === 0,
        criticalFailures,
        answers: aligned,
    };
}
//# sourceMappingURL=qm.js.map